import React, { useMemo } from 'react';
import { ActivityIndicator, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { usePortfolioRiskMonitor } from '../../services/queries/useRiskMonitor';

const pct = (value: number | null | undefined) =>
  value === null || value === undefined ? '—' : `${(value * 100).toFixed(1)}%`;

export function RiskWidget({ onPress }: { onPress: () => void }) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);
  const { data, isLoading, notReady, error } = usePortfolioRiskMonitor();
  // The ES fields exist only on the non-degraded limit payload; for degraded
  // data they are absent, which renders as '—' below.
  const limit = data && data.degraded !== true ? data.portfolio_limit : undefined;

  const status = !data
    ? error
      ? t('riskMonitor.loadError', {
          defaultValue: 'Risk monitor could not be loaded. Please try again later.',
        })
      : t('riskMonitor.notReady', {
          defaultValue: 'No risk monitor yet — calculated in the weekly review.',
        })
    : limit?.numeric_state === 'unknown'
      ? t('riskMonitor.notClassified', { defaultValue: 'Not classified' })
      : t(`riskMonitor.state_${limit?.numeric_state}`, {
          defaultValue: limit?.numeric_state?.replaceAll('_', ' ') || 'Unknown',
        });

  return (
    <TouchableOpacity
      style={styles.card}
      onPress={onPress}
      accessibilityRole="button"
      disabled={isLoading}
    >
      <Text style={styles.title}>{t('risk.title')}</Text>
      {isLoading ? (
        <ActivityIndicator color={theme.colors.primary} />
      ) : !data || notReady || error ? (
        <Text style={styles.notComputed}>{status}</Text>
      ) : (
        <>
          <View style={styles.metricsRow}>
            <Metric
              label={t('riskMonitor.currentEs', {
                defaultValue: 'Current ES (21-day, 95%)',
              })}
              value={pct(limit?.current_es_used)}
              styles={styles}
            />
            <Metric
              label={t('riskMonitor.limit', { defaultValue: 'Declared limit' })}
              value={pct(limit?.current_es_limit)}
              styles={styles}
            />
          </View>
          <Text style={styles.status}>{status}</Text>
          <Text style={styles.coverage}>
            {t('riskMonitor.coverageSummary', {
              defaultValue: '{{value}} of position value covered',
              value: pct(data.coverage.covered_value_fraction),
            })}
          </Text>
        </>
      )}
      <Text style={styles.link}>{t('risk.viewDetails')} ›</Text>
    </TouchableOpacity>
  );
}

function Metric({ label, value, styles }: { label: string; value: string; styles: any }) {
  return (
    <View style={styles.metric}>
      <Text style={styles.metricValue}>{value}</Text>
      <Text style={styles.metricLabel}>{label}</Text>
    </View>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    card: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.radii.lg,
      padding: theme.spacing.lg,
      borderWidth: 1,
      borderColor: theme.colors.border,
      marginVertical: theme.spacing.sm,
    },
    title: {
      color: theme.colors.text,
      fontSize: theme.typography.fontSizeLg,
      fontWeight: theme.typography.fontWeightBold,
      marginBottom: theme.spacing.md,
    },
    metricsRow: { flexDirection: 'row', justifyContent: 'space-between' },
    metric: { flex: 1 },
    metricValue: {
      color: theme.colors.text,
      fontSize: theme.typography.fontSizeXl,
      fontWeight: theme.typography.fontWeightBold,
    },
    metricLabel: {
      color: theme.colors.textSecondary,
      fontSize: theme.typography.fontSizeXs,
      marginTop: theme.spacing.xxs,
    },
    status: {
      color: theme.colors.text,
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
      marginTop: theme.spacing.sm,
    },
    coverage: {
      color: theme.colors.textSecondary,
      fontSize: theme.typography.fontSizeXs,
      marginTop: theme.spacing.xxs,
    },
    notComputed: { color: theme.colors.textTertiary, fontSize: theme.typography.fontSizeSm },
    link: {
      color: theme.colors.primary,
      fontSize: theme.typography.fontSizeSm,
      marginTop: theme.spacing.md,
    },
  });
}
